import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { movimentiApi } from '@/api/magazzino'

/**
 * Pagina lista movimenti magazzino.
 * Mostra storico movimenti con filtri per tipo e data.
 */
export default function MovimentiList() {
  const [search, setSearch] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('tutti')
  const [ordering, setOrdering] = useSortingState('movimenti', '-created_at')
  const [page, setPage] = useState(1)
  
  // Query movimenti
  const { data: movimentiData, isLoading } = useQuery({
    queryKey: ['movimenti-magazzino', { search, tipo: filtroTipo, ordering, page }],
    queryFn: () => {
      const params = { ordering, page }
      if (search) params.search = search
      if (filtroTipo !== 'tutti') params.tipo = filtroTipo
      return movimentiApi.getAll(params)
    },
  })

  const payload = movimentiData?.data
  const isPaginatedPayload = !!payload && !Array.isArray(payload) && Array.isArray(payload.results)

  const movimenti = Array.isArray(payload)
    ? payload
    : (payload?.results || [])

  const totalCount = isPaginatedPayload ? (payload.count ?? movimenti.length) : movimenti.length
  const hasPreviousPage = !!(isPaginatedPayload && payload.previous)
  const hasNextPage = !!(isPaginatedPayload && payload.next)

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getTipoIcon = (tipo) => {
    if (['carico', 'reso'].includes(tipo)) {
      return <ArrowUpIcon className="h-4 w-4 text-green-600" />
    }
    if (['scarico', 'vendita'].includes(tipo)) {
      return <ArrowDownIcon className="h-4 w-4 text-red-600" />
    }
    return <ClockIcon className="h-4 w-4 text-blue-600" />
  }

  const getTipoBadgeVariant = (tipo) => {
    if (['carico', 'reso'].includes(tipo)) return 'success'
    if (['scarico', 'vendita'].includes(tipo)) return 'destructive'
    return 'default'
  }

  const getDeltaScorta = (movimento) => {
    if (movimento.impatto_scorta === 'riservata') {
      if (movimento.tipo === 'riserva') return Math.abs(movimento.quantita)
      if (movimento.tipo === 'ripristino') return -Math.abs(movimento.quantita)
    }
    return movimento.quantita
  }

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
    setPage(1)
  }

  const handleFiltroTipoChange = (value) => {
    setFiltroTipo(value)
    setPage(1)
  }

  const handleSortChange = (value) => {
    setOrdering(value)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Movimenti Magazzino</h1>
          <p className="text-muted-foreground">
            Storico completo dei movimenti inventariali
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/magazzino/carico">
              <TrendingUp className="h-4 w-4 mr-2" />
              Carico
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/magazzino/scarico">
              <TrendingDown className="h-4 w-4 mr-2" />
              Scarico
            </Link>
          </Button>
        </div>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cerca per causale, ordine, lotto, prodotto, componente, box..."
                  value={search}
                  onChange={handleSearchChange}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-48">
              <Select value={filtroTipo} onValueChange={handleFiltroTipoChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo movimento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i tipi</SelectItem>
                  <SelectItem value="carico">Carico</SelectItem>
                  <SelectItem value="scarico">Scarico</SelectItem>
                  <SelectItem value="assemblaggio">Assemblaggio</SelectItem>
                  <SelectItem value="disassemblaggio">Disassemblaggio</SelectItem>
                  <SelectItem value="vendita">Vendita</SelectItem>
                  <SelectItem value="reso">Reso</SelectItem>
                  <SelectItem value="riserva">Riserva</SelectItem>
                  <SelectItem value="ripristino">Ripristino</SelectItem>
                  <SelectItem value="rettifica">Rettifica</SelectItem>
                  <SelectItem value="inventario">Inventario</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella Movimenti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5" />
            Movimenti ({totalCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Caricamento...
            </div>
          ) : movimenti.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nessun movimento trovato
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="created_at" currentOrdering={ordering} onSort={handleSortChange}>
                    Data/Ora
                  </SortableTableHead>
                  <SortableTableHead field="tipo" currentOrdering={ordering} onSort={handleSortChange}>Tipo</SortableTableHead>
                  <SortableTableHead field="item_nome" currentOrdering={ordering} onSort={handleSortChange}>Item</SortableTableHead>
                  <SortableTableHead field="variazione" currentOrdering={ordering} onSort={handleSortChange} className="text-center">Variazione</SortableTableHead>
                  <SortableTableHead field="impatto_scorta" currentOrdering={ordering} onSort={handleSortChange}>Impatto</SortableTableHead>
                  <SortableTableHead field="causale" currentOrdering={ordering} onSort={handleSortChange}>Causale</SortableTableHead>
                  <SortableTableHead field="operatore_nome" currentOrdering={ordering} onSort={handleSortChange}>Operatore</SortableTableHead>
                  <SortableTableHead field="riferimenti" currentOrdering={ordering} onSort={handleSortChange}>Riferimenti</SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movimenti.map((movimento) => {
                  const delta = getDeltaScorta(movimento)
                  return (
                  <TableRow key={movimento.id}>
                    <TableCell className="font-mono text-sm">
                      {formatDate(movimento.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getTipoBadgeVariant(movimento.tipo)} className="gap-1">
                        {getTipoIcon(movimento.tipo)}
                        {movimento.tipo_display}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{movimento.item_nome}</div>
                        <div className="text-sm text-muted-foreground font-mono">
                          {movimento.item_codice}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {movimento.item_tipo}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`font-bold ${
                        delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-muted-foreground'
                      }`}>
                        {delta > 0 ? '+' : ''}{delta}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {movimento.impatto_scorta === 'riservata' ? 'Riservata' : 'Disponibile'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate" title={movimento.causale}>
                        {movimento.causale}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {movimento.operatore_nome || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {movimento.riferimento_ordine && (
                        <div className="text-blue-600">Ord: {movimento.riferimento_ordine}</div>
                      )}
                      {movimento.riferimento_lotto && (
                        <div className="text-purple-600">Lotto: {movimento.riferimento_lotto}</div>
                      )}
                      {!movimento.riferimento_ordine && !movimento.riferimento_lotto && '-'}
                    </TableCell>
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          {isPaginatedPayload && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Pagina {page}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!hasPreviousPage || isLoading}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  Precedente
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!hasNextPage || isLoading}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
